import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Package, LogOut, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomerAccount = () => {
  const navigate = useNavigate();
  const { customer, token, isAuthenticated, logout, loading } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/account' } } });
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchOrders();
    }
  }, [isAuthenticated, token]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] pt-32 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#C4B5A0]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-seasons text-[#2C2C2C] mb-8">My Account</h1>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-white border-none shadow-lg p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-[#F5F3F0] rounded-full flex items-center justify-center mx-auto mb-3">
                  {customer?.picture ? (
                    <img src={customer.picture} alt={customer.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-[#C4B5A0]" />
                  )}
                </div>
                <h2 className="font-seasons text-xl text-[#2C2C2C]">{customer?.name}</h2>
                <p className="text-sm text-[#8B7D6B] font-montserrat">{customer?.email}</p>
              </div>

              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-montserrat text-sm transition-colors ${
                    activeTab === 'orders' ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
                  }`}
                >
                  <Package className="h-5 w-5" />
                  My Orders
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </button>
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-montserrat text-sm transition-colors ${
                    activeTab === 'profile' ? 'bg-[#F5F3F0] text-[#2C2C2C]' : 'text-[#8B7D6B] hover:bg-[#FAF9F7]'
                  }`}
                >
                  <User className="h-5 w-5" />
                  Profile
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </button>
              </nav>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full mt-6 border-red-300 text-red-500 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'orders' && (
              <div>
                <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-6">My Orders</h2>
                
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#C4B5A0]" />
                  </div>
                ) : orders.length === 0 ? (
                  <Card className="bg-white border-none shadow-lg p-8 text-center">
                    <Package className="h-12 w-12 text-[#C4B5A0] mx-auto mb-4" />
                    <h3 className="font-seasons text-xl text-[#2C2C2C] mb-2">No orders yet</h3>
                    <p className="text-[#8B7D6B] font-montserrat mb-6">Start shopping to see your orders here</p>
                    <Button
                      onClick={() => navigate('/')}
                      className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
                    >
                      Start Shopping
                    </Button>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <Card key={order.order_id} className="bg-white border-none shadow-lg p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                          <div>
                            <p className="font-montserrat text-xs text-[#8B7D6B]">Order ID</p>
                            <p className="font-seasons text-lg text-[#2C2C2C]">{order.order_id}</p>
                          </div>
                          <div>
                            <p className="font-montserrat text-xs text-[#8B7D6B]">Order Date</p>
                            <p className="font-montserrat text-sm text-[#2C2C2C]">
                              {new Date(order.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                          <div>
                            <p className="font-montserrat text-xs text-[#8B7D6B]">Total</p>
                            <p className="font-seasons text-lg text-[#2C2C2C]">₹{order.total_amount}</p>
                          </div>
                          <div>
                            <span className={`px-3 py-1 rounded-full text-xs font-montserrat uppercase ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                        </div>

                        <div className="border-t border-[#E8E5E0] pt-4">
                          <div className="flex gap-4 overflow-x-auto pb-2">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex-shrink-0">
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-16 h-20 object-cover rounded"
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-sm text-[#8B7D6B] font-montserrat mt-2">
                            {order.items.length} item{order.items.length > 1 ? 's' : ''}
                          </p>
                        </div>

                        {order.status === 'shipped' && (
                          <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                            <p className="text-sm text-purple-700 font-montserrat">
                              Your order is on the way! Expected delivery in 3-5 business days.
                            </p>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div>
                <h2 className="text-2xl font-seasons text-[#2C2C2C] mb-6">Profile Details</h2>
                <Card className="bg-white border-none shadow-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <p className="font-montserrat text-xs text-[#8B7D6B] mb-1">Full Name</p>
                      <p className="font-montserrat text-[#2C2C2C]">{customer?.name}</p>
                    </div>
                    <div>
                      <p className="font-montserrat text-xs text-[#8B7D6B] mb-1">Email</p>
                      <p className="font-montserrat text-[#2C2C2C]">{customer?.email}</p>
                    </div>
                    <div>
                      <p className="font-montserrat text-xs text-[#8B7D6B] mb-1">Phone</p>
                      <p className="font-montserrat text-[#2C2C2C]">{customer?.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="font-montserrat text-xs text-[#8B7D6B] mb-1">Account Type</p>
                      <p className="font-montserrat text-[#2C2C2C] capitalize">{customer?.auth_type || 'Email'}</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
